import {
    FeaturedContent,
    FeaturedJob,
    FeaturedReview,
} from 'rwl-shared/dist/models/neighborhood';
import { adaptV2FeaturedJob, adaptV2FeaturedReview } from './adaptV2Featured';
import { fetchV2NeighborhoodPage } from './fetchV2ServiceAreaList';
import { fetchV2ServiceAreaContentPage } from './fetchV2ServiceAreaContentPage';
import {
    V2NeighborhoodServiceArea,
    V2_EMBEDDED_JOBS_LIMIT,
    V2_EMBEDDED_REVIEWS_LIMIT,
    V2_NEIGHBORHOOD_MAX_LIMIT,
    V2_STORED_JOBS_LIMIT,
    V2_STORED_REVIEWS_LIMIT,
} from './v2NeighborhoodTypes';

/**
 * v2 replacement for the legacy `serviceareas/featured*` endpoints that back the
 * neighborhood widget's featured lists and job/review pop-out (EN-1328).
 *
 * Two shapes of v2 data feed this:
 * - `POST /widget/v2/neighborhood` — per area, embeds the top
 *   {@link V2_EMBEDDED_JOBS_LIMIT} jobs and {@link V2_EMBEDDED_REVIEWS_LIMIT}
 *   reviews. One all-areas call gives the selected area's first page *and* every
 *   other area's top content for the "nearby" fill.
 * - `POST /widget/v2/serviceArea/content` — pages a single area's jobs or
 *   reviews beyond the embedded window (the legacy "load more").
 *
 * Every exported fetcher returns `undefined` to mean "v2 could not answer" so
 * the caller falls back to v1 — matching the map fetchers' contract. An empty
 * result that v2 *did* produce is a normal answer, not a failure.
 */

/**
 * All service areas for an account with their embedded top jobs/reviews, in one
 * page at the max limit. Used for the featured list's page 1 (the selected
 * area's first screen) and its "nearby" fill (every other area's top content).
 *
 * A single page at {@link V2_NEIGHBORHOOD_MAX_LIMIT} covers up to 100 areas —
 * beyond any real account — so unlike the map's full-list walk this does not
 * page: the featured list only needs each area's embedded window, and the
 * selected area is fetched directly when it falls outside this set.
 */
const fetchAllAreasEmbedded = async ({
    neighborhoodUrl,
    accountId,
}: {
    neighborhoodUrl: string;
    accountId: string;
}): Promise<V2NeighborhoodServiceArea[] | undefined> => {
    const resp = await fetchV2NeighborhoodPage({
        url: neighborhoodUrl,
        body: { accountId, page: 1, limit: V2_NEIGHBORHOOD_MAX_LIMIT },
    });
    return resp?.serviceAreas;
};

const emptyFeaturedContent = (): FeaturedContent => ({
    jobs: [],
    reviews: [],
    similarJobs: [],
    similarReviews: [],
});

type FetchV2FeaturedContentPageParams = {
    neighborhoodUrl: string;
    serviceAreaContentUrl: string;
    accountId: string;
    postCode: string | undefined;
    /** 0-indexed widget page (matches the infinite query's `pageParam`). */
    pageParam: number;
};

/**
 * Build one page of `FeaturedContent` from v2.
 *
 * Postcode selected:
 * - page 0 → the area's embedded jobs/reviews as the main lists, every other
 *   area's embedded content as the "similar/nearby" fill.
 * - page N>0 → the area's `serviceArea/content` page `N+1` (page 1 there is the
 *   embedded window already rendered, so the client continues from page 2); no
 *   nearby, which belongs only to the first screen.
 *
 * No postcode (the default all-areas browse) → every area's embedded content
 * flattened into the main lists; no nearby, and no deep paging (there is no
 * cross-area "load more" in v2 — the embedded window per area is the set).
 *
 * Returns undefined only when v2 failed, so the caller falls back to v1 for that
 * page.
 */
export const fetchV2FeaturedContentPage = async ({
    neighborhoodUrl,
    serviceAreaContentUrl,
    accountId,
    postCode,
    pageParam,
}: FetchV2FeaturedContentPageParams): Promise<FeaturedContent | undefined> => {
    const hasPostCode = !!postCode && postCode.trim() !== '';

    if (!hasPostCode) {
        // All-areas browse: the embedded windows are the whole featured set.
        if (pageParam > 0) return emptyFeaturedContent();
        const areas = await fetchAllAreasEmbedded({
            neighborhoodUrl,
            accountId,
        });
        if (!areas) return undefined;
        return {
            jobs: areas.flatMap((a) => a.jobs).map(adaptV2FeaturedJob),
            reviews: areas.flatMap((a) => a.reviews).map(adaptV2FeaturedReview),
            similarJobs: [],
            similarReviews: [],
        };
    }

    if (pageParam === 0) {
        const areas = await fetchAllAreasEmbedded({
            neighborhoodUrl,
            accountId,
        });
        if (!areas) return undefined;

        let mainArea = areas.find((a) => a.postcode === postCode);
        if (!mainArea) {
            // The selected area ranked outside the top-100 set (very large
            // account). Fetch it directly so the main lists are never empty for
            // a served postcode.
            const resp = await fetchV2NeighborhoodPage({
                url: neighborhoodUrl,
                body: { accountId, page: 1, limit: 1, postcode: postCode },
            });
            if (!resp) return undefined;
            mainArea = resp.serviceAreas?.[0];
        }

        const otherAreas = areas.filter((a) => a.postcode !== postCode);
        return {
            jobs: (mainArea?.jobs ?? []).map(adaptV2FeaturedJob),
            reviews: (mainArea?.reviews ?? []).map(adaptV2FeaturedReview),
            similarJobs: otherAreas
                .flatMap((a) => a.jobs)
                .map(adaptV2FeaturedJob),
            similarReviews: otherAreas
                .flatMap((a) => a.reviews)
                .map(adaptV2FeaturedReview),
        };
    }

    // Deeper pages: continue the selected area from serviceArea/content page 2.
    const [jobsPage, reviewsPage] = await Promise.all([
        fetchV2ServiceAreaContentPage({
            url: serviceAreaContentUrl,
            body: {
                accountId,
                postcode: postCode,
                type: 'jobs',
                page: pageParam + 1,
                limit: V2_EMBEDDED_JOBS_LIMIT,
            },
        }),
        fetchV2ServiceAreaContentPage({
            url: serviceAreaContentUrl,
            body: {
                accountId,
                postcode: postCode,
                type: 'reviews',
                page: pageParam + 1,
                limit: V2_EMBEDDED_REVIEWS_LIMIT,
            },
        }),
    ]);

    // Fall back to v1 for the whole page if *either* type's fetch failed. A page
    // that failed returns undefined, whereas a type that is merely exhausted
    // returns a defined response with `data: []` — so this does not misfire on an
    // area that has run out of one type. Folding a failed page into `[]` would be
    // indistinguishable from "exhausted", silently truncating that type's
    // pagination with no retry.
    if (!jobsPage || !reviewsPage) return undefined;

    return {
        jobs: jobsPage.data.map(adaptV2FeaturedJob),
        reviews: reviewsPage.data.map(adaptV2FeaturedReview),
        similarJobs: [],
        similarReviews: [],
    };
};

/**
 * Resolve a single job's full detail from v2 by walking the selected area's
 * `serviceArea/content` jobs until the id matches. Page 1 is the embedded
 * window, so the walk starts there and covers the common case (a job clicked
 * before the featured list finished loading) in one request.
 *
 * Returns the adapted job when found; undefined when it cannot be resolved from
 * v2 — the area is unknown, the walk exhausted the stored jobs without a match
 * (a job past the stored cap, or a non-stored/unpublished pin), or a page
 * failed — so the caller falls back to the v1 by-id endpoint. The walk stops as
 * soon as `hasMore` is false, so it only reaches the cap for the largest areas.
 */
export const fetchV2FeaturedJobById = async ({
    serviceAreaContentUrl,
    accountId,
    postCode,
    jobId,
}: {
    serviceAreaContentUrl: string;
    accountId: string;
    postCode: string | undefined;
    jobId: string;
}): Promise<FeaturedJob | undefined> => {
    if (!postCode || postCode.trim() === '' || !jobId) return undefined;

    const maxPage = Math.ceil(V2_STORED_JOBS_LIMIT / V2_EMBEDDED_JOBS_LIMIT);
    for (let page = 1; page <= maxPage; page++) {
        const contentPage = await fetchV2ServiceAreaContentPage({
            url: serviceAreaContentUrl,
            body: {
                accountId,
                postcode: postCode,
                type: 'jobs',
                page,
                limit: V2_EMBEDDED_JOBS_LIMIT,
            },
        });
        if (!contentPage) return undefined;
        const match = contentPage.data.find((j) => j.id === jobId);
        if (match) return adaptV2FeaturedJob(match);
        if (!contentPage.hasMore) return undefined;
    }
    return undefined;
};

/** Reviews analogue of {@link fetchV2FeaturedJobById}. */
export const fetchV2FeaturedReviewById = async ({
    serviceAreaContentUrl,
    accountId,
    postCode,
    reviewId,
}: {
    serviceAreaContentUrl: string;
    accountId: string;
    postCode: string | undefined;
    reviewId: string;
}): Promise<FeaturedReview | undefined> => {
    if (!postCode || postCode.trim() === '' || !reviewId) return undefined;

    const maxPage = Math.ceil(
        V2_STORED_REVIEWS_LIMIT / V2_EMBEDDED_REVIEWS_LIMIT
    );
    for (let page = 1; page <= maxPage; page++) {
        const contentPage = await fetchV2ServiceAreaContentPage({
            url: serviceAreaContentUrl,
            body: {
                accountId,
                postcode: postCode,
                type: 'reviews',
                page,
                limit: V2_EMBEDDED_REVIEWS_LIMIT,
            },
        });
        if (!contentPage) return undefined;
        const match = contentPage.data.find((r) => r.id === reviewId);
        if (match) return adaptV2FeaturedReview(match);
        if (!contentPage.hasMore) return undefined;
    }
    return undefined;
};
